import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import { Button, Modal, Space } from 'antd';
import 'react-quill/dist/quill.snow.css';
import 'katex/dist/katex.min.css';
import 'mathlive/static.css';
import 'mathlive';
import katex from 'katex';
import './wysiwyg.scss';
import './wysiwyg-math.scss';
import ReactHtmlParser from "react-html-parser";
import { normalizeMathLatex } from './mathLatexNormalizer';

const FONT_WHITELIST = ['Inter', 'Italianno', 'Monoton', 'Pacifico', 'Righteous', 'Roboto', 'Sevillana'];
const SIZE_WHITELIST = ['14px', '16px', '20px', '24px', '32px', '42px', '54px', '68px'];
const QUICK_MATH_SNIPPETS = [
   '\\frac{a}{b}',
   '\\sqrt{x}',
   '\\sqrt[n]{x}',
   'x^2',
   'x_n',
   'x_{n+1}',
   '\\left( \\right)',
   '\\left[ \\right]',
   '\\left\\{ \\right\\}',
   '\\pi',
   '\\theta',
   '\\alpha',
   '\\beta',
   '\\gamma',
   '\\Delta',
   '\\infty',
   '\\sum_{i=1}^{n}',
   '\\prod_{i=1}^{n}',
   '\\int_{a}^{b}',
   '\\lim_{x \\to 0}',
   '\\dfrac{d}{dx}',
   '\\sin(x)',
   '\\cos(x)',
   '\\tan(x)',
   '\\log(x)',
   '\\ln(x)',
   '\\left|x\\right|',
   '\\vec{v}',
   '\\overline{AB}',
   '\\angle ABC',
   '\\leq',
   '\\geq',
   '\\neq',
   '\\approx',
   '\\equiv',
   '\\pm',
   '\\times',
   '\\div',
   '\\cdot',
   '\\in',
   '\\notin',
   '\\subseteq',
   '\\cup',
   '\\cap',
   '\\forall',
   '\\exists',
   '\\Rightarrow',
   '\\Leftrightarrow',
   // '\\begin{bmatrix} a & b \\\\ c & d \\end{bmatrix}',
   // '\\begin{cases} x^2, & x \\ge 0 \\\\ -x, & x < 0 \\end{cases}',
];

const PICKER_TOOLTIP_LABELS = {
   'ql-header': 'Text style',
   'ql-font': 'Font family',
   'ql-size': 'Font size',
   'ql-align': 'Text alignment',
   'ql-color': 'Text color',
   'ql-background': 'Text background color',
};

const PICKER_ITEM_TOOLTIP_LABELS = {
   'ql-header': {
      '1': 'Heading 1',
      '2': 'Heading 2',
      '3': 'Heading 3',
      '': 'Normal',
   },
};

const getToolbarControlTooltip = (el) => {
   if (!el || !el.classList) return null;
   const value = el.getAttribute('value');

   if (el.classList.contains('ql-bold')) return 'Bold';
   if (el.classList.contains('ql-italic')) return 'Italic';
   if (el.classList.contains('ql-underline')) return 'Underline';
   if (el.classList.contains('ql-strike')) return 'Strikethrough';
   if (el.classList.contains('ql-blockquote')) return 'Block quote';
   if (el.classList.contains('ql-code-block')) return 'Code block';
   if (el.classList.contains('ql-script') && value === 'sub') return 'Subscript';
   if (el.classList.contains('ql-script') && value === 'super') return 'Superscript';
   if (el.classList.contains('ql-list') && value === 'ordered') return 'Numbered list';
   if (el.classList.contains('ql-list') && value === 'bullet') return 'Bulleted list';
   if (el.classList.contains('ql-indent') && value === '-1') return 'Decrease indent';
   if (el.classList.contains('ql-indent') && value === '+1') return 'Increase indent';
   if (el.classList.contains('ql-link')) return 'Insert link';
   if (el.classList.contains('ql-image')) return 'Insert image';
   if (el.classList.contains('ql-video')) return 'Insert video';
   if (el.classList.contains('ql-insertMath')) return 'Insert math equation';
   if (el.classList.contains('ql-clean')) return 'Clear formatting';
   if (el.classList.contains('ql-header')) return 'Text style';
   if (el.classList.contains('ql-font')) return 'Font family';
   if (el.classList.contains('ql-size')) return 'Font size';
   if (el.classList.contains('ql-align')) return 'Text alignment';
   if (el.classList.contains('ql-color')) return 'Text color';
   if (el.classList.contains('ql-background')) return 'Text background color';

   return null;
};

let quillConfigured = false;
let wysiwygMathEditorInstanceCount = 0;

// Registers Quill formats/modules once per app session.
const configureQuill = () => {
   if (quillConfigured) return;

   const Quill = ReactQuill.Quill;
   const Formula = Quill.import('modules/formula');
   const Font = Quill.import('formats/font');
   const Size = Quill.import('attributors/style/size');

   Font.whitelist = FONT_WHITELIST;
   Size.whitelist = SIZE_WHITELIST;

   Quill.register(Formula, true);
   Quill.register(Font, true);
   Quill.register(Size, true);
   quillConfigured = true;
};

if (typeof window !== 'undefined' && !window.katex) {
   window.katex = katex;
}

// Rich-text editor with custom toolbar overflow controls and MathLive equation insertion.
export default function WysiwygMathEditor(props) {
   configureQuill();

   const quillRef = useRef(null);
   const mathFieldRef = useRef(null);
   const mathLatexRef = useRef('\\frac{a}{b}');
   const toolbarScrollRef = useRef(null);
   const instanceIdRef = useRef(null);

   if (!instanceIdRef.current) {
      wysiwygMathEditorInstanceCount += 1;
      instanceIdRef.current = wysiwygMathEditorInstanceCount;
   }

   const [editorValue, setEditorValue] = useState(props.content || '');
   const [mathModalOpen, setMathModalOpen] = useState(false);
   const [mathLatex, setMathLatex] = useState('\\frac{a}{b}');
   const [canScrollToolbarLeft, setCanScrollToolbarLeft] = useState(false);
   const [canScrollToolbarRight, setCanScrollToolbarRight] = useState(false);
   const [hasToolbarOverflow, setHasToolbarOverflow] = useState(false);
   const [toolbarElement, setToolbarElement] = useState(null);

   const editorId = `wysiwyg-math-${props.id || 'default'}-${instanceIdRef.current}`;
   const toolbarId = `wysiwyg-math-toolbar-${props.id || 'default'}-${instanceIdRef.current}`;
   // Keeps both the raw DOM ref (for measurements) and state ref (for ReactQuill toolbar binding) in sync.
   const setToolbarRefs = (node) => {
      toolbarScrollRef.current = node;
      setToolbarElement((prev) => (prev !== node ? node : prev));
   };

   useEffect(() => {
      setEditorValue(props.content || '');
   }, [props.content]);

   useEffect(() => {
      mathLatexRef.current = mathLatex;
   }, [mathLatex]);

   useEffect(() => {
      if (props.disabled) return undefined;

      const scrollEl = toolbarScrollRef.current;
      if (!scrollEl) return undefined;

      let frameId = null;

      // Computes left/right scroll affordances and whether overflow controls should be shown.
      const updateToolbarScrollState = () => {
         const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
         setHasToolbarOverflow(maxScrollLeft > 1);
         setCanScrollToolbarLeft(scrollEl.scrollLeft > 0);
         setCanScrollToolbarRight(scrollEl.scrollLeft < maxScrollLeft - 1);
      };

      // Coalesces rapid DOM/size events into one paint-aligned recalculation.
      const scheduleToolbarScrollStateUpdate = () => {
         if (frameId !== null) return;
         frameId = window.requestAnimationFrame(() => {
            frameId = null;
            updateToolbarScrollState();
         });
      };

      scrollEl.addEventListener('scroll', updateToolbarScrollState, { passive: true });
      window.addEventListener('resize', scheduleToolbarScrollStateUpdate);

      let resizeObserver = null;
      if (typeof ResizeObserver !== 'undefined') {
         resizeObserver = new ResizeObserver(scheduleToolbarScrollStateUpdate);
         resizeObserver.observe(scrollEl);
         Array.from(scrollEl.children).forEach((child) => resizeObserver.observe(child));
      }

      const mutationObserver = new MutationObserver(scheduleToolbarScrollStateUpdate);
      mutationObserver.observe(scrollEl, {
         childList: true,
         subtree: true,
         attributes: true,
         characterData: true,
      });

      scheduleToolbarScrollStateUpdate();

      return () => {
         if (frameId !== null) {
            window.cancelAnimationFrame(frameId);
         }
         scrollEl.removeEventListener('scroll', updateToolbarScrollState);
         window.removeEventListener('resize', scheduleToolbarScrollStateUpdate);
         mutationObserver.disconnect();
         if (resizeObserver) {
            resizeObserver.disconnect();
         }
      };
   }, [props.disabled, toolbarElement]);

   useEffect(() => {
      if (props.disabled) return undefined;

      const toolbar = document.getElementById(toolbarId);
      if (!toolbar) return undefined;

      const applyToolbarTooltips = () => {
         toolbar.querySelectorAll('button, select').forEach((control) => {
            const tooltip = getToolbarControlTooltip(control);
            if (tooltip && control.getAttribute('title') !== tooltip) {
               control.setAttribute('title', tooltip);
            }
         });

         Object.entries(PICKER_TOOLTIP_LABELS).forEach(([pickerClassName, tooltip]) => {
            toolbar.querySelectorAll(`.${pickerClassName} .ql-picker-label`).forEach((label) => {
               if (label.getAttribute('title') !== tooltip) {
                  label.setAttribute('title', tooltip);
               }
            });
         });

         Object.entries(PICKER_ITEM_TOOLTIP_LABELS).forEach(([pickerClassName, valueLabels]) => {
            toolbar.querySelectorAll(`.${pickerClassName} .ql-picker-item`).forEach((item) => {
               const itemValue = item.getAttribute('data-value') || '';
               const fallbackValue = item.getAttribute('value') || '';
               const tooltip = valueLabels[itemValue] || valueLabels[fallbackValue];
               if (tooltip && item.getAttribute('title') !== tooltip) {
                  item.setAttribute('title', tooltip);
               }
            });
         });
      };

      applyToolbarTooltips();

      const tooltipObserver = new MutationObserver(applyToolbarTooltips);
      tooltipObserver.observe(toolbar, {
         childList: true,
         subtree: true,
      });

      const scrollContainer = toolbar;

      let activeOptions = null;

      // Removes any temporary floating styles applied to an expanded Quill picker menu.
      const clearFloatingPicker = () => {
         if (!activeOptions) return;
         activeOptions.classList.remove('wysiwyg-math-floating-picker');
         activeOptions.style.position = '';
         activeOptions.style.top = '';
         activeOptions.style.left = '';
         activeOptions.style.minWidth = '';
         activeOptions.style.maxHeight = '';
         activeOptions.style.overflowY = '';
         activeOptions = null;
      };

      // Collapses the currently expanded picker when scrolling or context changes.
      const closeExpandedPicker = () => {
         const expandedPicker = toolbar.querySelector('.ql-picker.ql-expanded');
         if (!expandedPicker) return;
         expandedPicker.classList.remove('ql-expanded');
         clearFloatingPicker();
      };

      // Repositions expanded picker options as a viewport-level floating panel.
      const positionFloatingPicker = () => {
         const expandedPicker = toolbar.querySelector('.ql-picker.ql-expanded');
         if (!expandedPicker) {
            clearFloatingPicker();
            return;
         }

         const options = expandedPicker.querySelector('.ql-picker-options');
         const label = expandedPicker.querySelector('.ql-picker-label');
         if (!options || !label) {
            clearFloatingPicker();
            return;
         }

         const rect = label.getBoundingClientRect();
         const viewportPadding = 8;
         const optionsWidth = Math.max(rect.width, 140);
         const maxLeft = window.innerWidth - optionsWidth - viewportPadding;
         const nextLeft = Math.min(Math.max(rect.left, viewportPadding), Math.max(maxLeft, viewportPadding));
         const optionsHeight = options.offsetHeight || 240;
         let nextTop = rect.bottom + 4;
         if (nextTop + optionsHeight > window.innerHeight - viewportPadding) {
            nextTop = Math.max(viewportPadding, rect.top - optionsHeight - 4);
         }

         if (activeOptions && activeOptions !== options) {
            clearFloatingPicker();
         }

         activeOptions = options;
         activeOptions.classList.add('wysiwyg-math-floating-picker');
         activeOptions.style.position = 'fixed';
         activeOptions.style.left = `${nextLeft}px`;
         activeOptions.style.top = `${nextTop}px`;
         activeOptions.style.minWidth = `${optionsWidth}px`;
         activeOptions.style.maxHeight = '40vh';
         activeOptions.style.overflowY = 'auto';
      };

      const onPointerDown = () => {
         setTimeout(positionFloatingPicker, 0);
      };

      // Schedules picker positioning after Quill class toggles settle.
      const schedulePosition = () => {
         // Quill toggles classes asynchronously on interaction; two RAF ticks
         // makes picker positioning reliable across click/keyboard/touch paths.
         requestAnimationFrame(() => {
            requestAnimationFrame(positionFloatingPicker);
         });
      };

      const onScrollClose = () => {
         closeExpandedPicker();
      };

      const mutationObserver = new MutationObserver(schedulePosition);
      mutationObserver.observe(toolbar, {
         subtree: true,
         attributes: true,
         attributeFilter: ['class'],
      });

      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('click', schedulePosition, true);
      document.addEventListener('keydown', schedulePosition, true);
      window.addEventListener('scroll', onScrollClose, true);
      scrollContainer.addEventListener('scroll', onScrollClose, { passive: true });
      schedulePosition();

      return () => {
         tooltipObserver.disconnect();
         mutationObserver.disconnect();
         clearFloatingPicker();
         document.removeEventListener('pointerdown', onPointerDown, true);
         document.removeEventListener('click', schedulePosition, true);
         document.removeEventListener('keydown', schedulePosition, true);
         window.removeEventListener('scroll', onScrollClose, true);
         scrollContainer.removeEventListener('scroll', onScrollClose);
      };
   }, [props.disabled, toolbarId]);

   useEffect(() => {
      if (!mathModalOpen) {
         if (window.mathVirtualKeyboard) {
            window.mathVirtualKeyboard.visible = false;
            window.mathVirtualKeyboard.target = null;
         }
         return undefined;
      }

      const field = mathFieldRef.current;
      if (!field) return undefined;

      field.value = mathLatexRef.current || '\\frac{a}{b}';

      // Links MathLive's global virtual keyboard to the active math-field.
      const bindKeyboardTarget = () => {
         if (!window.mathVirtualKeyboard) return;
         window.mathVirtualKeyboard.layouts = 'default';
         window.mathVirtualKeyboard.target = field;
         window.mathVirtualKeyboard.visible = true;
      };

      // Syncs current MathLive value to React state for preview/insert flow.
      const syncLatex = () => {
         setMathLatex(normalizeMathLatex(field.value || ''));
      };

      // Restores keyboard targeting when the field regains focus.
      const handleFieldFocus = () => {
         bindKeyboardTarget();
      };

      // Routes virtual keyboard interactions back into the math field and state.
      const handleKeyboardPointer = (event) => {
         if (!event?.target || typeof event.target.closest !== 'function') return;
         if (!event.target.closest('.ML__keyboard')) return;

         setTimeout(() => {
            field.focus();
            bindKeyboardTarget();
            syncLatex();
         }, 0);
      };

      field.addEventListener('input', syncLatex);
      field.addEventListener('change', syncLatex);
      field.addEventListener('keyup', syncLatex);
      field.addEventListener('focusin', handleFieldFocus);
      field.addEventListener('click', handleFieldFocus);
      document.addEventListener('pointerdown', handleKeyboardPointer, true);

      setTimeout(() => {
         if (!field) return;
         field.focus();
         bindKeyboardTarget();
         if (typeof field.executeCommand === 'function') {
            field.executeCommand('showVirtualKeyboard');
         }
      }, 0);

      return () => {
         field.removeEventListener('input', syncLatex);
         field.removeEventListener('change', syncLatex);
         field.removeEventListener('keyup', syncLatex);
         field.removeEventListener('focusin', handleFieldFocus);
         field.removeEventListener('click', handleFieldFocus);
         document.removeEventListener('pointerdown', handleKeyboardPointer, true);
      };
   }, [mathModalOpen]);

   // Closes math modal and detaches MathLive virtual keyboard target.
   const closeMathModal = () => {
      if (window.mathVirtualKeyboard) {
         window.mathVirtualKeyboard.visible = false;
         window.mathVirtualKeyboard.target = null;
      }
      setMathModalOpen(false);
   };

   // Opens modal used to compose LaTeX equations via MathLive.
   const openMathModal = () => {
      setMathModalOpen(true);
   };

   // Builds Quill module config for editable vs read-only modes.
   const modules = useMemo(() => {
      if (props.disabled) {
         return {
            toolbar: false,
            clipboard: {
               matchVisual: false,
            },
         };
      }

      return {
         formula: true,
         toolbar: {
            container: toolbarElement,
            handlers: {
               insertMath: openMathModal,
            },
         },
         clipboard: {
            matchVisual: false,
         },
      };
   }, [props.disabled, toolbarElement]);

   // Pre-renders quick snippet LaTeX with KaTeX for fast visual snippet buttons.
   const renderedQuickSnippets = useMemo(
      () =>
         QUICK_MATH_SNIPPETS.map((snippet) => {
            try {
               return {
                  snippet,
                  renderedHtml: katex.renderToString(snippet, {
                     throwOnError: false,
                     displayMode: false,
                  }),
               };
            } catch (error) {
               return { snippet, renderedHtml: null };
            }
         }),
      []
   );

   const formats = [
      'header',
      'font',
      'size',
      'bold',
      'italic',
      'underline',
      'strike',
      'blockquote',
      'code-block',
      'script',
      'list',
      'bullet',
      'indent',
      'align',
      'direction',
      'color',
      'background',
      'link',
      // 'image',
      // 'video',
      'formula',
      'clean',
   ];

   // Emits HTML content to parent when callback is provided.
   const emitContent = (content) => {
      if (typeof props.changeContentCallback === 'function') {
         props.changeContentCallback(content);
      }
   };

   // Handles editor keystrokes and optionally pushes updates immediately.
   const handleEditorChange = (value) => {
      setEditorValue(value);
      // emitContent(value);

      // Optional behavior: push changes immediately (useful for draft persistence)
      if (props.updateOnChange) {
         props.changeContentCallback(value);
      }
   };

   // Pushes pending editor value on blur when using delayed update mode.
   const handleEditorBlur = () => {
      // emitContent(editorValue);
      // Default behavior: push changes only on blur
      if (!props.updateOnChange) {
         props.changeContentCallback(editorValue);
      }
   };

   // Inserts selected LaTeX as a Quill formula embed at current cursor position.
   const handleMathInsert = () => {
      const quill = quillRef.current?.getEditor();
      if (!quill) {
         setMathModalOpen(false);
         return;
      }

      // Normalising the LaTeX before it reaches Quill keeps Supplementary-Plane
      // math characters (U+1D465, U+1D466, ...) out of the saved HTML, where
      // MySQL/MariaDB `utf8mb3`/`latin1` storage would flatten them into a
      // literal "?".
      const latex = normalizeMathLatex(mathFieldRef.current?.value || mathLatex || '').trim();
      if (!latex) return;

      const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
      quill.insertEmbed(range.index, 'formula', latex, 'user');
      quill.insertText(range.index + 1, ' ', 'user');
      quill.setSelection(range.index + 2, 0, 'user');

      // const html = quill.root.innerHTML !== undefined ? quill.root.innerHTML : null;
      const html = quill.root.innerHTML;
      setEditorValue(html);
      emitContent(html);
      closeMathModal();
   };

   // Appends a snippet into the MathLive field using best available insert API.
   const appendQuickMath = (snippet) => {
      const field = mathFieldRef.current;
      if (!field) return;

      if (window.mathVirtualKeyboard) {
         window.mathVirtualKeyboard.target = field;
         window.mathVirtualKeyboard.visible = true;
      }

      if (typeof field.executeCommand === 'function') {
         field.executeCommand(['insert', snippet]);
         setMathLatex(normalizeMathLatex(field.value || ''));
      } else if (typeof field.insert === 'function') {
         field.insert(snippet, { insertionMode: 'replaceSelection' });
         setMathLatex(normalizeMathLatex(field.value || ''));
      } else {
         const currentLatex = field.value || '';
         const nextLatex = `${currentLatex}${snippet}`;
         field.value = nextLatex;
         setMathLatex(normalizeMathLatex(nextLatex));
      }

      field.focus();
      if (typeof field.executeCommand === 'function') {
         field.executeCommand('showVirtualKeyboard');
      }
   };

   // Smooth-scrolls the toolbar container horizontally by a fixed delta.
   const scrollToolbarBy = (deltaX) => {
      const scrollEl = toolbarScrollRef.current;
      if (!scrollEl) return;
      scrollEl.scrollBy({ left: deltaX, behavior: 'smooth' });
   };

   return (
      <div
         className='wysiwyg-math-editor'
         style={{ height: props.height, textAlign: 'left', background: 'white', borderRadius: '7px', border: '1px solid #ccc' }}
         onBlur={handleEditorBlur}
      >
         {!props.disabled && (
            <div className='wysiwyg-math-toolbar-shell'>
               {hasToolbarOverflow && (
                  <Button
                     type='primary'
                     size='small'
                     shape='circle'
                     onClick={() => scrollToolbarBy(-220)}
                     disabled={!canScrollToolbarLeft}
                     aria-label='Scroll toolbar left'
                     title='Scroll toolbar left'
                  >
                     {'<'}
                  </Button>
               )}

               <div id={toolbarId} ref={setToolbarRefs} className='wysiwyg-math-toolbar ql-toolbar ql-snow'>
                  <span className='ql-formats'>
                     <select className='ql-header' defaultValue=''>
                        <option value='1'>Heading 1</option>
                        <option value='2'>Heading 2</option>
                        <option value='3'>Heading 3</option>
                        <option value=''>Normal</option>
                     </select>
                     <select className='ql-font' defaultValue='Roboto'>
                        <option value=''>Default</option>
                        {FONT_WHITELIST.map((font) => (
                           <option key={font} value={font}>{font}</option>
                        ))}
                     </select>
                     <select className='ql-size' defaultValue='16px'>
                        {SIZE_WHITELIST.map((size) => (
                           <option key={size} value={size}>{size}</option>
                        ))}
                     </select>
                  </span>

                  <span className='ql-formats'>
                     <button type='button' className='ql-bold' />
                     <button type='button' className='ql-italic' />
                     <button type='button' className='ql-underline' />
                     <button type='button' className='ql-strike' />
                     <button type='button' className='ql-blockquote' />
                     <button type='button' className='ql-code-block' />
                  </span>

                  <span className='ql-formats'>
                     <button type='button' className='ql-script' value='sub' />
                     <button type='button' className='ql-script' value='super' />
                     <button type='button' className='ql-list' value='ordered' />
                     <button type='button' className='ql-list' value='bullet' />
                     <button type='button' className='ql-indent' value='-1' />
                     <button type='button' className='ql-indent' value='+1' />
                  </span>

                  <span className='ql-formats'>
                     <select className='ql-align' defaultValue='' />
                     <select className='ql-color' />
                     <select className='ql-background' />
                  </span>

                  <span className='ql-formats'>
                     <button type='button' className='ql-link' />
                     {/* <button type='button' className='ql-image' /> */}
                     {/* <button type='button' className='ql-video' /> */}
                     {/* <button type='button' className='ql-formula' /> */}
                     <button type='button' className='ql-insertMath'>
                        <span className='insert-math-btn-label'>Math</span>
                     </button>
                     <button type='button' className='ql-clean' />
                  </span>
               </div>

               {hasToolbarOverflow && (
                  <Button
                     type='primary'
                     shape='circle'
                     size='small'
                     onClick={() => scrollToolbarBy(220)}
                     disabled={!canScrollToolbarRight}
                     aria-label='Scroll toolbar right'
                     title='Scroll toolbar right'
                  >
                     {'>'}
                  </Button>
               )}
            </div>
         )}

         {(props.disabled || toolbarElement) && (
            <ReactQuill
               key={props.disabled ? `rq-disabled-${editorId}` : `rq-ready-${editorId}`}
               id={editorId}
               ref={quillRef}
               theme='snow'
               value={editorValue}
               onChange={handleEditorChange}
               modules={modules}
               formats={formats}
               readOnly={props.disabled}
               placeholder={props.placeholder || ''}
               style={{
                  width: '100%',
                  height: 'calc(100% - 55px)',
                  backgroundColor: 'white',
                  borderRadius: '7px',
                  // textAlign: 'left'
               }}
            />
         )}

         <Modal
            title='Math Equation Editor'
            open={mathModalOpen}
            onCancel={closeMathModal}
            maskClosable={false}
            width={920}
            destroyOnClose
            footer={[
               <Button shape='round' key='cancel' onClick={closeMathModal}>Cancel</Button>,
               <Button shape='round' key='insert' type='primary' onClick={handleMathInsert}>Insert Equation</Button>,
            ]}
         >
            <div className='math-editor-modal'>
               <div className='math-editor-help'>
                  Type LaTeX with the virtual keyboard, then insert the rendered equation into the editor.
               </div>

               <div style={{height: 103, overflowY: "auto", scrollbarWidth: "thin", background: '#FAFAFA', padding: 10, marginBottom: 5, border: '1px solid #d9d9d9', borderRadius: 7}}>
                  <Space wrap size={[8, 8]} className='math-snippet-list'>
                     {renderedQuickSnippets.map(({ snippet, renderedHtml }) => (
                        <Button
                           key={snippet}
                           size='large'
                           className='math-snippet-btn'
                           onClick={() => appendQuickMath(snippet)}
                           shape='round'
                        >
                           {renderedHtml ? (
                              <span
                                 className='math-snippet-render'
                                 // KaTeX output is trusted HTML generated from known local snippets.
                                 dangerouslySetInnerHTML={{ __html: renderedHtml }}                              
                              >
                                 {/* {ReactHtmlParser(renderedHtml)} */}
                              </span>
                           ) : (
                              snippet
                           )}
                        </Button>
                     ))}
                  </Space>
               </div>

               <div className='mathlive-container'>
                  <math-field
                     ref={mathFieldRef}
                     className='mathlive-input'
                     virtual-keyboard-mode='manual'
                     math-virtual-keyboard-policy='manual'
                     smart-mode='true'
                     default-mode='math'
                  />
               </div>
            </div>
         </Modal>
      </div>
   );
}
